import { forwardRef } from "react";
import { buildPrimitives, OPENINGS, visibleCells } from "@/lib/geometry";
import { Primitives } from "@/components/Primitives";
import { jointPrims, coupledJoints } from "@/lib/joints";
import { SheetFrame, F } from "@/components/sheets/SheetFrame";
import { hasPlan, planSketch } from "@/lib/corners";
import { infillsUsed } from "@/lib/glass";

const SCALES = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200];

export function pickScale(bounds, aw, ah) {
  const need = Math.max(bounds.w / aw, bounds.h / ah);
  return SCALES.find((s) => s >= need) || Math.ceil(need);
}

const PB = { x: 15, y: 222, w: 112, h: 52 };

const PlanBox = ({ plan }) => {
  const b = plan.bounds, kp = Math.min((PB.w - 6) / b.w, (PB.h - 10) / b.h);
  const ox = PB.x + (PB.w - b.w * kp) / 2 - b.x * kp, oy = PB.y + 7 + (PB.h - 8 - b.h * kp) / 2 - b.y * kp;
  return (
    <g data-testid="sheet-plan-sketch">
      <rect x={PB.x} y={PB.y} width={PB.w} height={PB.h} fill="none" stroke="#000" strokeWidth={0.25} />
      <text x={PB.x + 2} y={PB.y + 4.2} fontSize={2.6} fontFamily={F} fill="#000" fontWeight="bold">PLAN (TOP VIEW) · CORNER ANGLES · NTS</text>
      <g transform={`translate(${ox} ${oy}) scale(${kp})`}><Primitives prims={plan.prims} u={0.22 / kp} palette="print" font={F} /></g>
    </g>
  );
};

const InfillKey = ({ item }) => {
  const used = infillsUsed(item);
  if (!used.length) return null;
  let x = 30;
  const keys = used.map((m) => {
    const label = `${m.label} — ${m.spec}`.slice(0, 46), x0 = x;
    x += 7 + label.length * 1.42 + 5;
    return { m, label, x0 };
  }).filter((k) => k.x0 + 7 + k.label.length * 1.42 <= 290);
  return (
    <g data-testid="sheet-infill-key">
      <text x={16} y={277.4} fontSize={2.4} fontFamily={F} fill="#000" fontWeight="bold">INFILL:</text>
      {keys.map(({ m, label, x0 }) => (
        <g key={m.spec}>
          <rect x={x0} y={275} width={5} height={3} fill={m.print} stroke="#444" strokeWidth={0.15} />
          <text x={x0 + 6.4} y={277.4} fontSize={2.4} fontFamily={F} fill="#000">{label}</text>
        </g>
      ))}
    </g>
  );
};

export const SheetSVG = forwardRef(({ item, tb, sheetNo, total }, ref) => {
  const base = buildPrimitives(item, { dims: true, labels: false });
  const joints = coupledJoints(item);
  const prims = [...base.prims, ...jointPrims(item)], bounds = base.bounds;
  const plan = hasPlan(item) ? planSketch(item) : null;
  const aw = 275, ah = plan ? 188 : 240;
  const scale = pickScale(bounds, aw, ah);
  const k = 1 / scale;
  const ox = 15 + (aw - bounds.w * k) / 2 - bounds.x * k;
  const oy = 30 + (ah - bounds.h * k) / 2 - bounds.y * k;
  const used = [...new Set(visibleCells(item).map(({ r, c }) => item.cells[r][c]))];
  return (
    <SheetFrame svgRef={ref} item={item} tb={tb} sheetNo={sheetNo} total={total} suffix="E" kind="WINDOW & DOOR ELEVATION DRAWING"
      title="ELEVATION" subtitle={`${plan ? "DEVELOPED ELEVATION (UNFOLDED) · " : ""}VIEWED FROM OUTSIDE · ALL DIMENSIONS IN MM · SCALE 1:${scale}`} scaleText={`1:${scale} @ A3`} scaleValue={scale}>
      <g transform={`translate(${ox} ${oy}) scale(${k})`}>
        <Primitives prims={prims} u={0.18 / k / 1.3} palette="print" font={F} />
      </g>
      {plan && <PlanBox plan={plan} />}
      <InfillKey item={item} />
      <text x={16} y={281} fontSize={2.4} fontFamily={F} fill="#000">
        {`LEGEND: ${used.map((u) => `${OPENINGS.find((o) => o.value === u)?.code} = ${OPENINGS.find((o) => o.value === u)?.label}`).join("   ")}`}
      </text>
      <text x={16} y={284.5} fontSize={2} fontFamily={F} fill="#555">{`Dashed triangle apex indicates hinge side. Arrow indicates sliding direction.${joints.length ? `  ${joints.map((j) => `${j.mark} = coupling joint ${j.type.replace("+", " ↔ ")}`).join(" · ")} (see coupling details)` : ""}`}</text>
    </SheetFrame>
  );
});
SheetSVG.displayName = "SheetSVG";
